import { isEqual } from "es-toolkit";
import { useField } from "formik";
import { useCallback, useDeferredValue, useEffect } from "react";

import { useAssetUploader } from "@skilitics/assets";

import { useFieldContext } from "../../FormFieldContext.ts";
import type { FileDropzoneProps } from "../file-dropzone/index.ts";
import { useCommitChange, useFieldPlaceholder } from "../helpers.tsx";
import type { FileMediaAssetValue, MediaAssetValue } from "../types.ts";
import type { UploadOptions } from "./types.ts";

export const useMediaAssetUploadFileDropzoneProps = (
  setValue: (asset: FileMediaAssetValue | undefined) => void,
  options: UploadOptions,
): FileDropzoneProps => {
  const { mediaTypes, mimeType } = options;

  const uploader = useAssetUploader();

  const onFileSelected = useCallback(
    async (selectedFile: File) => {
      const file = options.transformFile ? await options.transformFile(selectedFile) : selectedFile;

      const others = options.onFileSelected ? await options.onFileSelected(file) : {};

      const { assetId: uploadedAssetId } = await uploader.receiveFile(file);

      setValue({ assetId: uploadedAssetId, name: file.name, source: "file", ...others });
    },
    [options, setValue, uploader],
  );

  const onFileDeselected = useCallback(() => {
    setValue(undefined);
  }, [setValue]);

  return {
    mediaTypes,
    mimeType,
    onFileSelected,
    onFileDeselected,
    onFileSelectionCancelled: uploader.cancelUpload,
  };
};

export interface FieldMediaAssetInputProps {
  id: string;
  dataTest?: string;
  name: string;
  placeholder: string | undefined;
  value: MediaAssetValue | undefined;
  setValue: (asset: MediaAssetValue | undefined) => void;
}

export const useFieldMediaAssetInputProps = (): FieldMediaAssetInputProps => {
  const { controlId, dataTest, name } = useFieldContext();
  const placeholder = useFieldPlaceholder(name);

  const [{ value }, _, { setValue }] = useField<MediaAssetValue | undefined>(name);

  // TODO: this is all a bit nasty and causes issues but keeping for now as more change elsewhere needed

  // no onBlur so we commit manually but have to use useEffect to defer to next render
  const oldValue = useDeferredValue(value);
  const commitChange = useCommitChange();

  useEffect(() => {
    // currently if you upload an asset there is no commit from the "controls"
    // but url assets are now via a behaving field so this hack causes havoc and is not needed there
    if (!isEqual(value, oldValue) && value?.source !== "url") {
      commitChange();
    }
  }, [value, commitChange, oldValue]);

  return {
    id: controlId,
    dataTest,
    name,
    placeholder,
    value,
    setValue,
  };
};
